// Warped audio at the room's tempo — the pure half (slice 48).
//
// Live stretches a warped clip so its beat markers land on the Set's grid
// whatever the tempo; the wire now says which tempo an object's markers
// are measured at (`ClipData.sourceTempo`), and the browser has to do the
// stretching itself. Here: the beat ↔ file-second maths of a warp map, the
// two plain entry points onto the stretch engine (lib/stretch — a
// phase-locked vocoder, which replaced the WSOLA that lived here), and a
// tempo detector for imported files (an onset envelope's autocorrelation,
// folded into the 80–160 range), so a dropped loop can be warped at its own
// tempo instead of played at the room's. Pure over channel arrays, testable
// without an AudioBuffer.

import type { PcmSource } from "@web-shared/lib/wav";
import { vocode } from "@web-shared/lib/stretch/vocoder";

/** Live's WarpMode.Repitch — the one mode where speed changes pitch. */
export const WARP_MODE_REPITCH = 3;

/** Speed factor a warped clip plays at: >1 is faster (a slower source in a
 * faster room). 1 when either tempo is unknown or they agree. */
export function tempoRatio(roomTempo: number, sourceTempo: number | undefined): number {
  if (sourceTempo === undefined || !(sourceTempo > 0) || !(roomTempo > 0)) return 1;
  const ratio = roomTempo / sourceTempo;
  return Math.abs(ratio - 1) < 1e-6 ? 1 : ratio;
}

/** A stretched result: the same shape `encodeWav24` and an AudioBuffer copy read. */
export interface StretchedPcm extends PcmSource {
  channels: Float32Array<ArrayBuffer>[];
}

/**
 * Time-stretch by `ratio` (the speed factor: 2 halves the length, 0.5
 * doubles it) without changing pitch, through the phase vocoder. A ratio
 * of 1 is a copy.
 */
export function stretch(source: PcmSource, ratio: number): StretchedPcm {
  if (!(ratio > 0) || Math.abs(ratio - 1) < 1e-6 || source.length === 0) return copyOf(source);
  return vocode(source, Math.max(1, Math.round(source.length / ratio)), (o) => o * ratio);
}

/**
 * Play a file onto a beat grid through Live's warp markers (slice 49): the
 * output runs at `bpm`, and output second t is the file at
 * `fileSecondsAt(markers, t·bpm/60)`. The vocoder reads each frame's
 * position off the map, so a tempo that changes between markers is
 * followed segment by segment. The output covers the
 * whole file: the beat the file ends at, at `bpm`.
 */
export function warpToGrid(source: PcmSource, markers: readonly WarpMarker[], bpm: number, fallbackBpm: number): StretchedPcm {
  const sr = source.sampleRate;
  if (!(bpm > 0) || source.length === 0) return copyOf(source);
  const endBeat = beatAtFileSeconds(markers, source.length / sr, fallbackBpm);
  const outLen = Math.max(1, Math.round((endBeat * 60) / bpm * sr));
  return vocode(source, outLen, (o) => fileSecondsAt(markers, ((o / sr) * bpm) / 60, fallbackBpm) * sr);
}

/** One of Live's warp markers on the wire: the clip's beat is that file second. */
export interface WarpMarker {
  beat: number;
  sec: number;
}

/**
 * The file second a clip beat sits at, through its markers: linear between
 * neighbours, the first or last segment's slope carried past the ends (as
 * Live carries its segment tempo), and `fallbackBpm` when fewer than two
 * markers say anything (one marker only anchors the offset).
 */
export function fileSecondsAt(markers: readonly WarpMarker[], beat: number, fallbackBpm: number): number {
  const secPerBeat = fallbackBpm > 0 ? 60 / fallbackBpm : 0.5;
  if (markers.length === 0) return beat * secPerBeat;
  if (markers.length === 1) return markers[0]!.sec + (beat - markers[0]!.beat) * secPerBeat;
  let i = 1;
  while (i < markers.length - 1 && markers[i]!.beat < beat) i++;
  const a = markers[i - 1]!;
  const b = markers[i]!;
  const span = b.beat - a.beat;
  const slope = span > 0 ? (b.sec - a.sec) / span : secPerBeat;
  return a.sec + (beat - a.beat) * slope;
}

/** The inverse: which clip beat a file second is at (the map is monotonic). */
export function beatAtFileSeconds(markers: readonly WarpMarker[], sec: number, fallbackBpm: number): number {
  const beatsPerSec = fallbackBpm > 0 ? fallbackBpm / 60 : 2;
  if (markers.length === 0) return sec * beatsPerSec;
  if (markers.length === 1) return markers[0]!.beat + (sec - markers[0]!.sec) * beatsPerSec;
  let i = 1;
  while (i < markers.length - 1 && markers[i]!.sec < sec) i++;
  const a = markers[i - 1]!;
  const b = markers[i]!;
  const span = b.sec - a.sec;
  const slope = span > 0 ? (b.beat - a.beat) / span : beatsPerSec;
  return a.beat + (sec - a.sec) * slope;
}

function copyOf(source: PcmSource): StretchedPcm {
  const channels = Math.max(1, source.numberOfChannels);
  return pcmOf(
    source.sampleRate,
    Array.from({ length: channels }, (_, c) => {
      const d = source.getChannelData(Math.min(c, source.numberOfChannels - 1));
      const copy = new Float32Array(new ArrayBuffer(d.length * 4));
      copy.set(d);
      return copy;
    }),
  );
}

function monoOf(inputs: Float32Array[], len: number): Float32Array {
  if (inputs.length === 1) return inputs[0]!;
  const mono = new Float32Array(new ArrayBuffer(len * 4));
  for (const ch of inputs) for (let i = 0; i < len; i++) mono[i] = (mono[i] ?? 0) + (ch[i] ?? 0) / inputs.length;
  return mono;
}

function pcmOf(sampleRate: number, channels: Float32Array<ArrayBuffer>[]): StretchedPcm {
  return {
    sampleRate,
    numberOfChannels: channels.length,
    length: channels[0]?.length ?? 0,
    channels,
    getChannelData: (c) => channels[c] ?? channels[0]!,
  };
}

// ---- Tempo detection -------------------------------------------------------

export interface TempoEstimate {
  /** Beats per minute, folded into [MIN_FOLD_BPM, MAX_FOLD_BPM] when the
   * neighbouring octave supports it. */
  bpm: number;
  /** 0..1 — how much the best lag stands out from the rest of the range.
   * Below `CONFIDENT_TEMPO` the file has no usable pulse (a pad, speech, a
   * single hit) and a caller should fall back to the room's tempo. */
  confidence: number;
}

/** The least confidence worth warping an import at. */
export const CONFIDENT_TEMPO = 0.12;

export { AUTO_WARP_MIN_BEATS, autoWarps } from "@shared/playback/geometry.js";
import { autoWarps } from "@shared/playback/geometry.js";

/**
 * The tempo an imported file is warped at, or null to leave it unwarped
 * (Live's default: everything a bar or longer is warped): what its name or
 * loop length states; else what was heard, when clear and a bar or more at
 * that tempo; else the room's own tempo (warped, unstretched) when the
 * file is a bar or more at THAT tempo. Shorter is a hit. Pure — the
 * callers decode and listen.
 */
export function autoWarpTempo(
  input: { durationSec: number; stated: number | null; heard: TempoEstimate | null },
  roomTempo: number,
): number | null {
  const { durationSec, stated, heard } = input;
  if (stated !== null && stated >= 20 && stated <= 999) return stated;
  if (heard && heard.confidence >= CONFIDENT_TEMPO && autoWarps((durationSec * heard.bpm) / 60)) return heard.bpm;
  if (roomTempo > 0 && autoWarps((durationSec * roomTempo) / 60)) return roomTempo;
  return null;
}
const MIN_BPM = 60;
const MAX_BPM = 200;
const MIN_FOLD_BPM = 80;
const MAX_FOLD_BPM = 160;
const HOP = 256;
const FRAME = 1024;
const MIN_SECONDS = 2;

/**
 * The tempo of a file, if it has one: an onset-strength envelope (the
 * half-wave-rectified rise of each frame's log energy) autocorrelated over
 * the lags of 60–200 BPM. The beat period is the lag whose MULTIPLES
 * correlate too — one, two, three and four beats (metricalScore): a loud
 * part repeating at some other interval (an arpeggio, a chop's dotted
 * pattern) peaks at its own lag but doesn't line up with the bar, so it
 * can't outvote the beat. The winner is refined between its neighbours.
 * A read outside 80–160 is folded an
 * octave when the folded lag correlates nearly as well (a 64 BPM read of a
 * 128 loop). Null for a file too short to hold two beats.
 */
export function detectTempo(source: PcmSource): TempoEstimate | null {
  const sr = source.sampleRate;
  const len = source.length;
  if (!(sr > 0) || len < MIN_SECONDS * sr) return null;
  const channels = Math.max(1, source.numberOfChannels);
  const inputs = Array.from({ length: channels }, (_, c) => source.getChannelData(Math.min(c, source.numberOfChannels - 1)));
  const mono = monoOf(inputs, len);

  const frames = Math.floor((len - FRAME) / HOP) + 1;
  if (frames < 8) return null;
  const onset = new Float32Array(new ArrayBuffer(frames * 4));
  let prev = 0;
  for (let f = 0; f < frames; f++) {
    let energy = 0;
    const at = f * HOP;
    for (let i = 0; i < FRAME; i++) {
      const v = mono[at + i] ?? 0;
      energy += v * v;
    }
    const level = Math.log1p((energy / FRAME) * 1e4);
    onset[f] = Math.max(0, level - prev);
    prev = level;
  }
  // Remove the envelope's mean so a steady level does not correlate with itself.
  let mean = 0;
  for (let f = 0; f < frames; f++) mean += onset[f]!;
  mean /= frames;
  for (let f = 0; f < frames; f++) onset[f] = onset[f]! - mean;

  const lagOf = (bpm: number): number => (60 / bpm) * (sr / HOP);
  const minLag = Math.max(1, Math.floor(lagOf(MAX_BPM)));
  const maxLag = Math.min(frames - 2, Math.ceil(lagOf(MIN_BPM)));
  if (maxLag <= minLag) return null;
  let zero = 0;
  for (let f = 0; f < frames; f++) zero += onset[f]! * onset[f]!;
  if (zero <= 1e-12) return null;
  // Out to four beats of the slowest tempo, for the metrical score.
  const corrLag = Math.min(frames - 2, BEAT_MULTIPLES.length * maxLag + 2);
  const corr = new Float32Array(new ArrayBuffer((corrLag + 1) * 4));
  for (let lag = minLag; lag <= corrLag; lag++) {
    let sum = 0;
    for (let f = 0; f + lag < frames; f++) sum += onset[f]! * onset[f + lag]!;
    corr[lag] = sum / zero;
  }
  let bestLag = minLag;
  let bestScore = Number.NEGATIVE_INFINITY;
  let total = 0;
  for (let lag = minLag; lag <= maxLag; lag++) {
    total += Math.max(0, corr[lag]!);
    const score = metricalScore(corr, lag, corrLag) * tempoPrior((60 * sr) / (HOP * lag));
    if (score > bestScore) {
      bestScore = score;
      bestLag = lag;
    }
  }
  // A dotted figure — every 1½ beats, or a dotted eighth's ¾ — can outscore
  // the beat under it. When the beat that figure implies scores nearly as
  // well, that's the beat.
  const figure = bestLag;
  let under = { lag: -1, score: bestScore * DOTTED_MARGIN };
  for (const ratio of DOTTED_RATIOS) {
    // The implied beat falls between integer lags; take the better neighbour.
    const at = figure * ratio;
    for (const beat of [Math.floor(at), Math.ceil(at)]) {
      if (beat < minLag || beat > maxLag) continue;
      const score = metricalScore(corr, beat, corrLag) * tempoPrior((60 * sr) / (HOP * beat));
      if (score >= under.score) under = { lag: beat, score };
    }
  }
  if (under.lag > 0) bestLag = under.lag;
  const bestVal = corr[bestLag]!;
  if (!(bestVal > 0)) return null;
  const averaged = total / (maxLag - minLag + 1);
  const confidence = Math.max(0, Math.min(1, bestVal - averaged));

  // The period, measured where it is sharpest: one frame of error over four
  // beats is a quarter of one over a single beat.
  const refined = refinePeriod(corr, bestLag, minLag, corrLag, bestVal);
  let bpm = (60 * sr) / (HOP * refined);
  // Fold an octave when the range wants it and the folded lag agrees.
  const strengthAt = (b: number): number => {
    const l = lagOf(b);
    const i = Math.round(l);
    return i >= minLag && i <= maxLag ? (corr[i] ?? 0) : 0;
  };
  if (bpm < MIN_FOLD_BPM && bpm * 2 <= MAX_BPM && strengthAt(bpm * 2) >= bestVal * 0.5) bpm *= 2;
  else if (bpm > MAX_FOLD_BPM && bpm / 2 >= MIN_BPM && strengthAt(bpm / 2) >= bestVal * 0.5) bpm /= 2;
  return { bpm: Math.round(bpm * 100) / 100, confidence: Math.round(confidence * 1000) / 1000 };
}

/** Where listeners put the beat, given a choice of levels: a gentle bell
 * around 120 BPM, an octave wide in log tempo. It breaks near-ties between a
 * beat and a slower pattern over it (a dotted chop every 1½ beats, a bar of
 * three) and can't outvote a clear pulse. */
const PRIOR_CENTRE_BPM = 120;
const PRIOR_OCTAVES = 1;

function tempoPrior(bpm: number): number {
  const x = Math.log2(bpm / PRIOR_CENTRE_BPM) / PRIOR_OCTAVES;
  return Math.exp(-0.5 * x * x);
}

/** Beat lag ÷ figure lag for the dotted figures a beat can hide under: one
 * every 1½ beats, and a dotted eighth (¾ of a beat). */
const DOTTED_RATIOS = [2 / 3, 4 / 3] as const;
/** How nearly the beat under a dotted figure must score to be taken over it. */
const DOTTED_MARGIN = 0.75;

/** How much a candidate beat's 1st–4th multiples count: the beat itself, the
 * half bar, three beats, the bar. */
const BEAT_MULTIPLES = [1, 0.75, 0.5, 0.75] as const;

/**
 * A candidate beat period scored by how well the envelope repeats at it and at
 * its multiples. An integer lag drifts off a fractional period by up to half a
 * frame per multiple, so the k-th multiple takes the best of ±⌊k/2⌋ frames.
 * Multiples past the end of the file are left out of the average rather than
 * counted as zero, so a short file doesn't favour fast tempos.
 */
function metricalScore(corr: Float32Array, lag: number, corrLag: number): number {
  let sum = 0;
  let weights = 0;
  BEAT_MULTIPLES.forEach((w, i) => {
    const k = i + 1;
    const reach = Math.floor(k / 2);
    const at = k * lag;
    if (at + reach > corrLag) return;
    let peak = Number.NEGATIVE_INFINITY;
    for (let d = -reach; d <= reach; d++) peak = Math.max(peak, corr[at + d] ?? 0);
    sum += w * peak;
    weights += w;
  });
  return weights > 0 ? sum / weights : corr[lag]!;
}

/**
 * The beat period to a fraction of a frame: the peak at the highest multiple
 * of `lag` that still correlates (at least half as well as the beat itself),
 * located between its neighbours and divided back down.
 */
function refinePeriod(corr: Float32Array, lag: number, minLag: number, corrLag: number, beatVal: number): number {
  for (let k = BEAT_MULTIPLES.length; k > 1; k--) {
    const reach = Math.floor(k / 2);
    const at = k * lag;
    if (at + reach + 1 > corrLag) continue;
    let peak = at;
    for (let d = -reach; d <= reach; d++) if ((corr[at + d] ?? 0) > (corr[peak] ?? 0)) peak = at + d;
    if ((corr[peak] ?? 0) < beatVal * 0.5) continue;
    return refineLag(corr, peak, minLag, corrLag) / k;
  }
  return refineLag(corr, lag, minLag, corrLag);
}

function refineLag(corr: Float32Array, lag: number, minLag: number, maxLag: number): number {
  if (lag <= minLag || lag >= maxLag) return lag;
  const a = corr[lag - 1]!;
  const b = corr[lag]!;
  const c = corr[lag + 1]!;
  const denom = a - 2 * b + c;
  if (Math.abs(denom) < 1e-12) return lag;
  const shift = (0.5 * (a - c)) / denom;
  return lag + Math.max(-0.5, Math.min(0.5, shift));
}
